import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Eye, EyeOff } from "lucide-react";

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: "" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const label = ["Very weak", "Weak", "OK", "Good", "Strong", "Excellent"][score];
  return { score, label };
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = (params.get("token") || "").trim();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const strength = useMemo(() => passwordStrength(pw1), [pw1]);

  useEffect(() => {
    if (!token) setErr("This reset link is missing its token. Please request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw1.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw1 !== pw2) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      if (auth?.refetchUser) await auth.refetchUser();
      toast.success("Password updated. You're signed in.");
      setTimeout(() => navigate("/", { replace: true }), 1500);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="reset-password-page">
      <div className="border-2 border-[#09090b] bg-white p-8">
        <div className="flex items-center gap-2 mb-6">
          <KeyRound className="w-6 h-6 text-[#ea580c]" />
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#ea580c]">Set a new password</div>
        </div>
        {done ? (
          <div data-testid="reset-password-done">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-6 h-6 text-[#16a34a]" />
              <h1 className="font-display text-2xl font-black tracking-tight">Password updated</h1>
            </div>
            <p className="text-sm text-[#52525b]">
              You're now signed in. Redirecting you to the homepage…
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-black tracking-tight mb-2">Choose a new password</h1>
            <p className="text-sm text-[#52525b] mb-6">
              For your security, pick a password with at least 8 characters. Mix upper/lowercase, numbers and a symbol for the strongest protection.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <label className="block">
                <div className="text-xs font-bold uppercase tracking-wider text-[#52525b] mb-1.5">New password</div>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    data-testid="reset-password-new"
                    className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2.5 pr-10 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#71717a] hover:text-[#09090b]"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid="toggle-password-visibility"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {pw1 && (
                  <div className="mt-2" data-testid="password-strength">
                    <div className="flex gap-1">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 ${i < strength.score
                            ? strength.score <= 2 ? "bg-[#dc2626]" : strength.score <= 3 ? "bg-[#facc15]" : "bg-[#16a34a]"
                            : "bg-[#e4e4e7]"}`}
                        />
                      ))}
                    </div>
                    <div className="text-[10px] mt-1 font-bold uppercase tracking-wider text-[#71717a]">{strength.label}</div>
                  </div>
                )}
              </label>
              <label className="block">
                <div className="text-xs font-bold uppercase tracking-wider text-[#52525b] mb-1.5">Confirm password</div>
                <input
                  type={show ? "text" : "password"}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-password-confirm"
                  className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2.5 outline-none"
                />
              </label>
              {err && (
                <div className="text-sm text-[#dc2626] border border-[#dc2626] bg-[#fef2f2] px-3 py-2" data-testid="reset-password-error">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !token}
                data-testid="reset-password-submit"
                className="w-full bg-[#ea580c] hover:bg-[#c2410c] disabled:opacity-60 text-white font-bold py-3"
              >
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
        <div className="mt-6 text-sm text-center text-[#52525b]">
          <Link to="/login" className="font-bold text-[#ea580c] hover:underline" data-testid="back-to-login">Back to login</Link>
        </div>
      </div>
    </div>
  );
}
